// File: frontend/src/pages/KnowledgeGraph/types.ts

// --- API Response Types ---

export interface KGStatsResponse {
  reports: number;
  canon_entities: number;
  observed_rel: number;
  inferred_rel_all: number;
  inferred_rel_high_conf: number;
  top_entities: KGTopEntity[];
  type_distribution: Record<string, number>;
}

export interface KGTopEntity {
  id: string;
  name: string;
  type: EntityType;
  report_count: number;
}

export interface KGSearchResult {
  id: string;
  name: string;
  type: EntityType;
  aliases?: string[];
  external_ids?: string[];
  report_count: number;
}

export interface KGSearchResponse {
  count: number;
  results: KGSearchResult[];
}

export interface KGNode {
  id: string;
  name: string;
  type: EntityType;
  report_count?: number;
  aliases?: string[];
  external_ids?: string[];
}

export interface KGEdge {
  source: string;
  target: string;
  type: string;
  edge_kind: 'OBSERVED_REL' | 'INFERRED_REL';
  confidence?: number;
  mention_count?: number;
}

export interface KGClusterResponse {
  node_count: number;
  edge_count: number;
  nodes: KGNode[];
  edges: KGEdge[];
}

export interface KGTimelineAppearance {
  report_id: string;
  title: string;
  date: string;
  source: string;
  surface_form: string;
}

export interface KGTimelineResponse {
  name: string;
  type: EntityType;
  aliases: string[];
  total_mentions: number;
  appearances: KGTimelineAppearance[];
}

export interface KGAttackPathResponse {
  count: number;
  results: KGNode[];
}

export interface KGCrossReportEntity {
  id: string;
  name: string;
  type: EntityType;
}

export interface KGCrossReportResponse {
  count: number;
  shared_entities: KGCrossReportEntity[];
}

// --- UI Types ---

export type EntityType =
  | 'ThreatActor'
  | 'Malware'
  | 'Tool'
  | 'AttackPattern'
  | 'Campaign'
  | 'Identity'
  | 'Indicator'
  | 'Vulnerability';

export interface EntityTypeOption {
  value: EntityType | '';
  label: string;
}

export const ENTITY_TYPE_OPTIONS: EntityTypeOption[] = [
  { value: '', label: 'All Types' },
  { value: 'ThreatActor', label: 'Threat Actor' },
  { value: 'Malware', label: 'Malware' },
  { value: 'Tool', label: 'Tool' },
  { value: 'AttackPattern', label: 'Attack Pattern' },
  { value: 'Campaign', label: 'Campaign' },
  { value: 'Vulnerability', label: 'Vulnerability' },
];

// Aligned with the "cyber command center" theme tokens (see src/theme.ts):
// error red, accent cyan/green/amber, primaryLight purple.
export const ENTITY_TYPE_COLORS: Record<string, string> = {
  ThreatActor: '#ff5c5c',
  Malware: '#ff9f45',
  Tool: '#4dd0e1',
  AttackPattern: '#9d7bff',
  Campaign: '#5dd39e',
  Identity: '#9fa4c0',
  Indicator: '#c9cee6',
  Vulnerability: '#fbbf24',
};

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  ThreatActor: 'Threat Actor',
  Malware: 'Malware',
  Tool: 'Tool',
  AttackPattern: 'Attack Pattern',
  Campaign: 'Campaign',
  Identity: 'Identity',
  Indicator: 'Indicator',
  Vulnerability: 'Vulnerability',
};

export const ENTITY_TYPE_SHAPES: Record<string, string> = {
  ThreatActor: 'diamond',
  Malware: 'ellipse',
  Tool: 'round-rectangle',
  AttackPattern: 'triangle',
  Campaign: 'hexagon',
  Identity: 'ellipse',
  Indicator: 'ellipse',
  Vulnerability: 'round-rectangle',
};

export interface ExampleQuery {
  term: string;
  subtitle: string;
}

export const EXAMPLE_QUERIES: ExampleQuery[] = [
  { term: 'APT28', subtitle: 'Russian state-sponsored actor' },
  { term: 'PowerShell', subtitle: 'Most connected tool (667+ relations)' },
  { term: 'Lazarus Group', subtitle: 'DPRK threat actor cluster' },
  { term: 'Ransomware', subtitle: 'Ransomware ecosystem' },
  { term: 'Supply Chain', subtitle: 'Supply chain attack patterns' },
  { term: 'T1059', subtitle: 'Command & Scripting Interpreter' },
];

export const MAX_GRAPH_NODES = 150;
